/**
 * Auth React hooks (TanStack Query)
 *
 * useLogin       — login mutation, onSuccess'da auth.store ga yozadi
 * useMe          — joriy user (faqat token bo'lsa)
 * useLogout      — auth.store ni tozalash
 * useAuth        — auth.store getter (selector)
 */

import type { ImpersonateInput, LoginInput, UserDto } from '@avtofix/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useAuthStore } from '@/app/stores/auth.store';

import { authApi, decodeJwtPayload } from './auth.api';

const ME_QUERY_KEY = ['auth', 'me'] as const;

export function useLogin() {
  const login = useAuthStore((s) => s.login);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: (response) => {
      const payload = decodeJwtPayload(response.token);
      if (!payload) {
        throw new Error("JWT token noto'g'ri formatda");
      }
      login({ token: response.token, user: response.user, payload });
      // Cache'ni invalidate qilish
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });
}

export function useMe() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const cachedUser = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: authApi.getMe,
    enabled: isAuthenticated,
    initialData: cachedUser ?? undefined,
    staleTime: 60_000, // 1 daqiqa
  });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const queryClient = useQueryClient();

  return useCallback(() => {
    logout();
    queryClient.clear();
  }, [logout, queryClient]);
}

export function useCurrentUser(): UserDto | null {
  return useAuthStore((s) => s.user);
}

export function useIsAuthenticated(): boolean {
  return useAuthStore((s) => s.isAuthenticated());
}

/**
 * useImpersonate — boshqa xodim sifatida kirish (Login As)
 *
 * Muvaffaqiyat: yangi token bilan auth store yangilanadi (originalUserId bilan).
 * Caller `/kassa` ga navigate qilishi kerak (spec bo'lim 16).
 */
export function useImpersonate() {
  const login = useAuthStore((s) => s.login);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ImpersonateInput) => authApi.impersonate(input),
    onSuccess: (response) => {
      const payload = decodeJwtPayload(response.token);
      if (!payload) {
        throw new Error("JWT token noto'g'ri formatda");
      }
      login({ token: response.token, user: response.user, payload });
      queryClient.clear();
    },
  });
}

/**
 * useStopImpersonation — asl hisobga qaytish
 */
export function useStopImpersonation() {
  const login = useAuthStore((s) => s.login);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.stopImpersonation(),
    onSuccess: (response) => {
      const payload = decodeJwtPayload(response.token);
      if (!payload) {
        throw new Error("JWT token noto'g'ri formatda");
      }
      login({ token: response.token, user: response.user, payload });
      queryClient.clear();
    },
  });
}

/**
 * useIsImpersonating — hozir impersonation ichidamiz (token originalUserId bor)
 */
export function useIsImpersonating(): boolean {
  return useAuthStore((s) => s.tokenPayload?.originalUserId !== undefined);
}

/**
 * useImpersonationOrigin — impersonation paytida asl user ma'lumotlari
 *
 * Zustand selector har renderda yangi obyekt qaytmasligi uchun, 2 ta
 * primitive selector bilan quramiz (referential equality React uchun muhim).
 */
export function useImpersonationOrigin(): { userId: string; role: UserDto['role'] } | null {
  const originalUserId = useAuthStore((s) => s.tokenPayload?.originalUserId);
  const originalRole = useAuthStore((s) => s.tokenPayload?.originalRole);
  return useMemo(() => {
    if (originalUserId === undefined || originalRole === undefined) {
      return null;
    }
    return { userId: originalUserId, role: originalRole };
  }, [originalUserId, originalRole]);
}
